import React, { useEffect, useState } from "react";
import { Files, PencilSimpleLine, DotsThree, LinkBreak, Handshake } from "@phosphor-icons/react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import type { CollaborationNote } from "types/collaborationNote";
import useGetCollaborationNotes from "hooks/api/client/useGetCollaborationNotes";
import useCreateCollaborationNote from "hooks/api/client/useCreateCollaborationNote";
import useUpdateCollaborationNote from "hooks/api/client/useUpdateCollaborationNote";
import CollaborationNoteDialog from "./CollaborationNoteDialog";
import CollaborationNotesDrawer from "./CollaborationNotesDrawer";

interface ProviderListItemMenuProps {
  onEdit: () => void;
  onCreateROI: () => void;
  onRemove: () => void;
  userPermissions: any;
  editMode: boolean;
  providerId: string;
  clientId: string | undefined;
  onRecentCollaborationChange: (hasRecent: boolean) => void;
}

/**
 * Renders provider row actions with collaboration note workflow.
 *
 * @param props - Component props.
 * @returns Rendered provider action menu.
 */
const ProviderListItemMenu: React.FC<ProviderListItemMenuProps> = ({
  onEdit,
  onCreateROI,
  onRemove,
  userPermissions,
  editMode,
  providerId,
  clientId,
  onRecentCollaborationChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [collaborationDialogOpen, setCollaborationDialogOpen] = useState(false);
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<CollaborationNote | null>(null);
  const [hasMenuOpened, setHasMenuOpened] = useState(false);

  const { notes, notesLoading, hasRecentCollaboration } = useGetCollaborationNotes(
    clientId,
    providerId,
    { enabled: hasMenuOpened },
  );
  const { createNote, isCreating } = useCreateCollaborationNote();
  const { updateNote, isUpdating } = useUpdateCollaborationNote();

  useEffect(() => {
    onRecentCollaborationChange(hasRecentCollaboration);
  }, [hasRecentCollaboration, onRecentCollaborationChange]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setHasMenuOpened(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditProvider = () => {
    handleClose();
    onEdit();
  };

  const handleCreateROIForm = () => {
    handleClose();
    onCreateROI();
  };

  const handleRemoveProvider = () => {
    handleClose();
    onRemove();
  };

  const handleAddCollaborationNote = () => {
    handleClose();
    setCollaborationDialogOpen(true);
  };

  const handleViewCollaborationNotes = () => {
    handleClose();
    setNotesDrawerOpen(true);
  };

  const handleCloseCollaborationDialog = () => {
    setCollaborationDialogOpen(false);
    setEditingNote(null);
  };

  const handleEditNote = (note: CollaborationNote) => {
    setEditingNote(note);
    setCollaborationDialogOpen(true);
  };

  const handleSaveCollaborationNote = async (noteBody: string) => {
    if (!clientId) return;
    try {
      if (editingNote) {
        await updateNote({
          clientId,
          providerId,
          noteId: editingNote.id,
          body: noteBody,
        });
      } else {
        await createNote({ clientId, providerId, body: noteBody });
      }
      setCollaborationDialogOpen(false);
      setEditingNote(null);
    } catch {
      // Error toast handled by mutation hooks' onError callbacks
    }
  };

  return (
    <>
      <IconButton
        aria-controls={`provider-menu-${providerId}`}
        aria-haspopup="true"
        aria-label="Open provider actions menu"
        onClick={handleClick}
        size="small"
      >
        <DotsThree size={32} />
      </IconButton>
      <Menu
        id={`provider-menu-${providerId}`}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {editMode ? (
          <>
            <MenuItem onClick={handleEditProvider}>
              <PencilSimpleLine size={20} style={{ marginRight: "1rem" }} weight="duotone" /> Edit
              Provider
            </MenuItem>
            <MenuItem
              onClick={handleCreateROIForm}
              disabled={!userPermissions?.create_phi_release_request}
            >
              <Files size={20} style={{ marginRight: "1rem" }} weight="duotone" /> Create Release of
              Information Form
            </MenuItem>
            <MenuItem onClick={handleAddCollaborationNote}>
              <Handshake size={20} style={{ marginRight: "1rem" }} weight="duotone" /> Add
              Collaboration Note
            </MenuItem>
            <MenuItem onClick={handleRemoveProvider}>
              <LinkBreak size={20} style={{ marginRight: "1rem" }} weight="duotone" /> Remove
              Provider from Client Account
            </MenuItem>
          </>
        ) : (
          <MenuItem
            onClick={handleViewCollaborationNotes}
            disabled={notes.length === 0 || notesLoading}
          >
            <Handshake size={20} style={{ marginRight: "1rem" }} weight="duotone" /> View
            Collaboration Notes
          </MenuItem>
        )}
      </Menu>

      <CollaborationNoteDialog
        open={collaborationDialogOpen}
        onClose={handleCloseCollaborationDialog}
        onSave={handleSaveCollaborationNote}
        noteToEdit={editingNote ?? undefined}
        isSaving={isCreating || isUpdating}
      />

      <CollaborationNotesDrawer
        open={notesDrawerOpen}
        onClose={() => setNotesDrawerOpen(false)}
        notes={notes}
        onEditNote={handleEditNote}
        isLoading={notesLoading}
      />
    </>
  );
};

export default ProviderListItemMenu;
